// webExtract.js
/**
 * Advanced Image Extraction Utility for News Articles
 * Optimized for RSS feeds with reliable image data (BBC, Reuters, ABC, etc.)
 */

/**
 * Validate if URL is a proper image URL
 */
export const isValidImageUrl = (url) => {
  if (!url || typeof url !== 'string' || url.length < 10) return false;
  
  try {
    let testUrl = url;
    if (url.startsWith('//')) testUrl = 'https:' + url;
    else if (url.startsWith('/')) return false;
    
    const urlObj = new URL(testUrl);
    if (!['http:', 'https:'].includes(urlObj.protocol)) return false;
    
    const pathname = urlObj.pathname.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'];
    const hasImageExt = imageExtensions.some(ext => pathname.endsWith(`.${ext}`));
    
    // CDN domains are usually reliable even without extensions
    const cdnDomains = ['cloudinary', 'imgix', 'cloudfront', 'akamai', 'fastly', 'unsplash', 'images.', 'img.', 'media.'];
    const isCdn = cdnDomains.some(cdn => urlObj.hostname.includes(cdn));
    
    if (isCdn || hasImageExt) return true;
    
    // Image-like paths
    if (pathname.includes('image') || pathname.includes('img') || pathname.includes('photo') || pathname.includes('pic')) {
      return true;
    }
    
    return hasImageExt;
  } catch (e) {
    return false;
  }
};

/**
 * Clean and normalize image URL
 */
const cleanImageUrl = (url) => {
  if (!url) return null;
  url = url.trim();
  
  if (url.startsWith('//')) url = 'https:' + url;
  
  try {
    const urlObj = new URL(url);
    // Keep CDN URLs intact
    if (!url.includes('cloudinary') && !url.includes('imgix') && !url.includes('unsplash')) {
      const essentialParams = ['w', 'h', 'width', 'height', 'quality', 'format', 'fit'];
      const newParams = new URLSearchParams();
      for (const [key, value] of urlObj.searchParams) {
        if (essentialParams.includes(key.toLowerCase())) {
          newParams.set(key, value);
        }
      }
      urlObj.search = newParams.toString();
      return urlObj.toString();
    }
  } catch (e) {}
  
  return url;
};

/**
 * STRATEGY 1: Extract image from RSS feed XML (primary method)
 */
const extractImageFromRSSItem = (item) => {
  try {
    // Try 1: Media content (most common in BBC, Reuters, etc.)
    const mediaContent = item.querySelector('media\\:content, content');
    if (mediaContent) {
      const url = mediaContent.getAttribute('url');
      if (url && isValidImageUrl(url)) {
        return cleanImageUrl(url);
      }
    }
    
    // Try 2: Media thumbnail
    const thumbnail = item.querySelector('media\\:thumbnail, thumbnail');
    if (thumbnail) {
      const url = thumbnail.getAttribute('url');
      if (url && isValidImageUrl(url)) {
        return cleanImageUrl(url);
      }
    }
    
    // Try 3: Enclosure tag (common in many feeds)
    const enclosure = item.querySelector('enclosure');
    if (enclosure) {
      const type = enclosure.getAttribute('type');
      if (type && type.startsWith('image/')) {
        const url = enclosure.getAttribute('url');
        if (url && isValidImageUrl(url)) {
          return cleanImageUrl(url);
        }
      }
    }
    
    // Try 4: Description/content with embedded images
    const description = item.querySelector('description, content\\:encoded, summary')?.textContent;
    if (description) {
      // Multiple patterns for finding images in description
      const imgPatterns = [
        /<img[^>]+src=["']([^"']+)["']/gi,
        /<img[^>]+data-src=["']([^"']+)["']/gi,
        /src=["']([^"']*\.(jpg|jpeg|png|gif|webp)[^"']*)["']/gi
      ];
      
      for (const pattern of imgPatterns) {
        const matches = [...description.matchAll(pattern)];
        for (const match of matches) {
          const url = match[1];
          if (url && isValidImageUrl(url)) {
            // Skip tiny images (icons, logos, tracking pixels)
            if (!url.includes('1x1') && 
                !url.includes('icon') && 
                !url.includes('logo') &&
                !url.includes('pixel') &&
                !url.includes('spacer')) {
              return cleanImageUrl(url);
            }
          }
        }
      }
    }
    
    // Try 5: Image tag in content
    const contentImage = item.querySelector('image url');
    if (contentImage) {
      const url = contentImage.textContent?.trim();
      if (url && isValidImageUrl(url)) {
        return cleanImageUrl(url);
      }
    }
    
    return null;
  } catch (e) {
    console.error('Error extracting image from RSS item:', e);
    return null;
  }
};

/**
 * STRATEGY 2: Extract image from article meta tags via CORS proxy
 */
const extractImageFromArticle = async (url) => {
  try {
    const proxies = [
      `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
      `https://corsproxy.io/?${encodeURIComponent(url)}`
    ];
    
    let html = null;
    
    // Try each proxy with timeout
    for (const proxyUrl of proxies) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        const response = await fetch(proxyUrl, {
          method: 'GET',
          signal: controller.signal,
          headers: { 'Accept': 'text/html' }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) continue;
        
        html = await response.text();
        if (html && html.length > 0) break;
      } catch (err) {
        continue;
      }
    }
    
    if (!html || html.length === 0) return null;
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Try Open Graph image (most reliable)
    let imageUrl = doc.querySelector('meta[property="og:image"]')?.getAttribute('content') ||
                   doc.querySelector('meta[property="og:image:secure_url"]')?.getAttribute('content');
    
    if (imageUrl && isValidImageUrl(imageUrl)) {
      return cleanImageUrl(imageUrl);
    }
    
    // Try Twitter Card image
    imageUrl = doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content') ||
               doc.querySelector('meta[name="twitter:image:src"]')?.getAttribute('content');
    
    if (imageUrl && isValidImageUrl(imageUrl)) {
      return cleanImageUrl(imageUrl);
    }
    
    // Try generic image meta
    imageUrl = doc.querySelector('meta[name="image"]')?.getAttribute('content') ||
               doc.querySelector('meta[itemprop="image"]')?.getAttribute('content');
    
    if (imageUrl && isValidImageUrl(imageUrl)) {
      return cleanImageUrl(imageUrl);
    }
    
    // Try link rel="image_src"
    imageUrl = doc.querySelector('link[rel="image_src"]')?.getAttribute('href');
    
    if (imageUrl && isValidImageUrl(imageUrl)) {
      return cleanImageUrl(imageUrl);
    }
    
    return null;
    
  } catch (error) {
    return null;
  }
};

/**
 * STRATEGY 3: Smart Unsplash fallback based on article keywords
 */
const getUnsplashFallback = async (title, source) => {
  try {
    const stopWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'a', 'an', 'is', 'was', 'are'];
    
    // Extract meaningful keywords
    const keywords = title.toLowerCase()
      .split(/\s+/)
      .filter(word => word.length > 3 && !stopWords.includes(word))
      .slice(0, 3)
      .join(',');
    
    if (!keywords) return getCategoryPlaceholder(source, title);
    
    return `https://source.unsplash.com/800x600/?${keywords}`;
  } catch (e) {
    return getCategoryPlaceholder(source, title);
  }
};

/**
 * STRATEGY 4: High-quality category-based placeholder images
 */
const getCategoryPlaceholder = (source, title) => {
  const text = (source + ' ' + title).toLowerCase();
  
  const categoryMap = {
    // Technology & AI
    technology: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80',
    ai: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
    'artificial intelligence': 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80',
    software: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80',
    programming: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=800&q=80',
    coding: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=800&q=80',
    
    // Business & Finance
    business: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
    finance: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80',
    stock: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
    market: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
    economy: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80',
    
    // Science & Health
    science: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800&q=80',
    health: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80',
    medical: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800&q=80',
    medicine: 'https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?w=800&q=80',
    research: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=800&q=80',
    
    // Gaming & Entertainment
    gaming: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&q=80',
    game: 'https://images.unsplash.com/photo-1538481199705-c710c4e965fc?w=800&q=80',
    entertainment: 'https://images.unsplash.com/photo-1514306191717-452ec28c7814?w=800&q=80',
    movie: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
    film: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80',
    
    // Sports
    sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80',
    football: 'https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=800&q=80',
    basketball: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&q=80',
    soccer: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800&q=80',
    
    // Automotive
    car: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80',
    electric: 'https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800&q=80',
    vehicle: 'https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80',
    
    // Crypto & Blockchain
    crypto: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=800&q=80',
    bitcoin: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=800&q=80',
    blockchain: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&q=80',
    
    // World & Politics
    world: 'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=800&q=80',
    politics: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800&q=80',
    war: 'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=800&q=80',
    conflict: 'https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=800&q=80'
  };
  
  // Find best matching category
  for (const [key, url] of Object.entries(categoryMap)) {
    if (text.includes(key)) return url;
  }
  
  // Default fallback - newspaper/news
  return 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
};

/**
 * Main image extraction with multi-strategy fallback
 */
const extractArticleImage = async (article, xmlItem = null) => {
  // STRATEGY 1: Try RSS feed image first (fastest and most reliable)
  if (xmlItem) {
    const feedImage = extractImageFromRSSItem(xmlItem);
    if (feedImage) {
      console.log(`  ✓ RSS image: ${article.title.substring(0, 40)}`);
      return feedImage;
    }
  }
  
  // STRATEGY 2: Try fetching meta tags from article URL (slower but works sometimes)
  if (article.url && !article.url.includes('news.google.com')) {
    try {
      const metaImage = await extractImageFromArticle(article.url);
      if (metaImage) {
        console.log(`  ✓ Meta image: ${article.title.substring(0, 40)}`);
        return metaImage;
      }
    } catch (e) {}
  }
  
  // STRATEGY 3: Use Unsplash with smart keywords
  try {
    const unsplashImage = await getUnsplashFallback(article.title, article.source);
    console.log(`  ✓ Unsplash: ${article.title.substring(0, 40)}`);
    return unsplashImage;
  } catch (e) {}
  
  // STRATEGY 4: Category placeholder (always works)
  const placeholderImage = getCategoryPlaceholder(article.source, article.title);
  console.log(`  ✓ Placeholder: ${article.title.substring(0, 40)}`);
  return placeholderImage;
};

/**
 * Enrich search results with images using parallel processing
 */
export const enrichSearchResultsWithImages = async (results, timeoutMs = 8000) => {
  try {
    if (!results || results.length === 0) {
      console.log('No results to enrich');
      return results;
    }
    
    console.log(`🖼️ Extracting images for ${results.length} articles...`);
    
    // Create timeout promise
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), timeoutMs)
    );

    // Process articles with staggered requests to avoid overwhelming proxies
    const enrichPromise = Promise.all(
      results.map(async (result, index) => {
        try {
          // Stagger requests by 200ms
          await new Promise(resolve => setTimeout(resolve, index * 200));
          
          const imageUrl = await extractArticleImage(result, result.xmlItem);
          
          return {
            ...result,
            imageUrl: imageUrl || getCategoryPlaceholder(result.source, result.title)
          };
          
        } catch (error) {
          console.warn(`Failed to get image for article ${index}`);
          return {
            ...result,
            imageUrl: getCategoryPlaceholder(result.source, result.title)
          };
        }
      })
    );

    // Race between enrichment and timeout
    try {
      const enrichedResults = await Promise.race([enrichPromise, timeoutPromise]);
      const successCount = enrichedResults.filter(r => r.imageUrl).length;
      console.log(`✅ Images extracted: ${successCount}/${results.length}`);
      return enrichedResults;
      
    } catch (error) {
      if (error.message === 'Timeout') {
        console.warn('⏱️ Timeout - using fallbacks');
        return results.map(r => ({
          ...r,
          imageUrl: getCategoryPlaceholder(r.source, r.title)
        }));
      }
      throw error;
    }

  } catch (error) {
    console.error('Error enriching results:', error);
    return results.map(r => ({
      ...r,
      imageUrl: getCategoryPlaceholder(r.source, r.title)
    }));
  }
};

export default {
  enrichSearchResultsWithImages,
  extractImageFromArticle,
  isValidImageUrl
};